import MainLayout from "@/components/layout/MainLayout";
import ApproverGuard from "@/config/guard/ApproverGuard";
import HomeRedirect from "@/config/guard/HomeRedirect";
import InstructionGuard from "@/config/guard/InstructionGuard";
import ManagerGuard from "@/config/guard/ManagerGuard";
import { UserProvider } from "@/contexts/UserContext";
import DepartmentListPage from "@/pages/approver/departments";
import ApproverStatistics from "@/pages/approver/instruction/statistics";
import UserListPage from "@/pages/approver/users";
import DirectiveFormPage from "@/pages/common/DirectiveFormPage";
import DepartmentHeadsListPage from "@/pages/approver/department-heads";
import UnauthorizedPage from "@/pages/error/401";
import ForbiddenPage from "@/pages/error/403";
import NotFoundPage from "@/pages/error/404";
import Instruction from "@/pages/instruction";
import ManagerStatistics from "@/pages/manager/instruction/statistics";
import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";

// 1. rootRoute에서 바로 MainLayout 사용
const rootRoute = createRootRoute({
  component: () => (
    <UserProvider>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </UserProvider>
  ),
});

// 2. 실제 페이지 라우트들(rootRoute의 children)
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRedirect,
});

const instructionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "instructions",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      type: search.type as string | undefined,
      conditions: search.conditions as Record<string, unknown> | undefined,
      page: search.page as number | undefined,
      size: search.size as number | undefined,
      sortBy: search.sortBy as string | undefined,
      direction: search.direction as string | undefined,
    };
  },
  component: InstructionGuard,
});

const instructionListRoute = createRoute({
  getParentRoute: () => instructionRoute,
  path: "/",
  component: Instruction,
});

const instructionCreateRoute = createRoute({
  getParentRoute: () => instructionRoute,
  path: "create",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      type: search.type as string | undefined,
      conditions: search.conditions as Record<string, unknown> | undefined,
      page: search.page as number | undefined,
      size: search.size as number | undefined,
      sortBy: search.sortBy as string | undefined,
      direction: search.direction as string | undefined,
    };
  },
  component: () => <DirectiveFormPage routeId='/instructions/create' />,
});

const managerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "manager",
  component: ManagerGuard,
});

const managerInstructionStatisticsRoute = createRoute({
  getParentRoute: () => managerRoute,
  path: "instructions/statistics",
  component: ManagerStatistics,
});

const approverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "approver",
  component: ApproverGuard,
});

const approverInstructionStatisticsRoute = createRoute({
  getParentRoute: () => approverRoute,
  path: "instructions/statistics",
  component: ApproverStatistics,
});

const instructionDetailRoute = createRoute({
  getParentRoute: () => instructionRoute,
  path: "$id",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      type: search.type as string | undefined,
      conditions: search.conditions as Record<string, unknown> | undefined,
      page: search.page as number | undefined,
      size: search.size as number | undefined,
      sortBy: search.sortBy as string | undefined,
      direction: search.direction as string | undefined,
    };
  },
  component: () => <DirectiveFormPage routeId='/instructions/$id' />,
});

const userManagementRoute = createRoute({
  getParentRoute: () => approverRoute,
  path: "users",
  component: UserListPage,
});

const departmentManagementRoute = createRoute({
  getParentRoute: () => approverRoute,
  path: "departments",
  component: DepartmentListPage,
});

const departmentHeadsRoute = createRoute({
  getParentRoute: () => approverRoute,
  path: "department-heads",
  component: DepartmentHeadsListPage,
});

// 3. 에러/404 라우트는 반드시 rootRoute의 children에!
const unauthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/error/401",
  component: UnauthorizedPage,
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/error/403",
  component: ForbiddenPage,
});
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "*",
  component: NotFoundPage,
});

// 4. 라우터 트리
const routeTree = rootRoute.addChildren([
  homeRoute,
  instructionRoute.addChildren([instructionListRoute, instructionCreateRoute, instructionDetailRoute]),
  managerRoute.addChildren([managerInstructionStatisticsRoute]),
  approverRoute.addChildren([
    approverInstructionStatisticsRoute,
    userManagementRoute,
    departmentManagementRoute,
    departmentHeadsRoute,
  ]),
  unauthorizedRoute,
  forbiddenRoute,
  notFoundRoute,
]);

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
});

export { router };
